import { InputField } from 'lib/iterable-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { BUTTON_SIZES, GhostSuccessButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { spacing } from 'ui/theme';
import { ESP_ACCOUNT_PAYLOAD_FIELDS } from '../../../types';

const { EDIT_API_KEY, EDIT_SIGNING_SECRET, EDIT_CLIENT_SECRET } = ESP_ACCOUNT_PAYLOAD_FIELDS;

const ClearableInputWrapper = styled.div`
  display: flex;
  gap: ${spacing.base};
`;

const StyledInputField = styled(InputField)`
  flex-grow: 1;
`;

export type ClearableConnectedInputFieldProps = {
  labelText: string;
  name: string;
  placeholder?: string;
  wasEdited: boolean;
  tripWasEdited: () => void;
};

export const BaseClearableConnectedInputField = ({
  labelText,
  name,
  wasEdited,
  tripWasEdited,
  ...props
}: ClearableConnectedInputFieldProps) => {
  const {
    setValue,
    formState: { errors },
  } = useFormContext();

  const inputId = `${name}-input`;
  const errorText = (errors[name]?.message || '') as string;

  const handleClear = () => {
    tripWasEdited();
    setValue(name, '');
  };

  const disabled = !wasEdited;

  return (
    <FormField
      inputId={inputId}
      labelText={labelText}
      errorText={errorText}
      hasError={!!errorText}
      isDisabled={disabled}
    >
      <ClearableInputWrapper>
        <StyledInputField name={name} id={inputId} isDisabled={disabled} {...props} />
        <GhostSuccessButton
          onClick={handleClear}
          size={BUTTON_SIZES.LARGE}
          data-test={`${name}-clear-button`}
        >
          {`Clear ${labelText}`}
        </GhostSuccessButton>
      </ClearableInputWrapper>
    </FormField>
  );
};

export const ClearableConnectedInputField = ({
  wasEditedFieldName,
  ...props
}: Omit<ClearableConnectedInputFieldProps, 'wasEdited' | 'tripWasEdited'> & {
  wasEditedFieldName: typeof EDIT_API_KEY | typeof EDIT_SIGNING_SECRET | typeof EDIT_CLIENT_SECRET;
}) => {
  const { setValue } = useFormContext();
  const wasEdited = useWatch({ name: wasEditedFieldName });
  const tripWasEdited = () => setValue(wasEditedFieldName, true);
  return (
    <BaseClearableConnectedInputField
      {...props}
      wasEdited={wasEdited}
      tripWasEdited={tripWasEdited}
    />
  );
};
